import { AppTable, AppTableProps } from 'components/organisms/AppTable';
import { commonTexts, settlementTexts } from 'consts/text';
import { ChildTransactionListItem } from 'types';
import { settlementChildPaymentDTOFields } from 'consts/settlementChildren/settlementChildrenDTOFields';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { getChildPaymentTransactionTypeText } from 'utils/getChildPaymentTransactionTypeText';
import { getFormattedPrice } from 'utils/getFormattedPrice';
import { ChildTransactionDescriptionDisplay } from 'components/molecules';
import { childPaymentFormTypeLabels } from 'consts/settlementChildren/childPaymentFormTypeLabels';
import { childBalanceTargetTypeLabels } from 'consts/settlementChildren/childBalanceTargetTypeLabels';
import { childSourceTypeLabels } from 'consts/settlementChildren/childSourceTypeLabels';

type ChildTransactionTableProps = Pick<
    AppTableProps<ChildTransactionListItem>,
    | 'options'
    | 'isError'
    | 'onAddButtonClick'
    | 'pagination'
    | 'dataSource'
    | 'isDataRefetching'
    | 'isDataInitialLoading'
    | 'isHiddenAddButton'
>;

const columns: AppTableProps<ChildTransactionListItem>['columns'] = [
    {
        title: commonTexts.dataLabels.date,
        dataIndex: settlementChildPaymentDTOFields.dateTime,
        key: settlementChildPaymentDTOFields.dateTime,
        isVisibleAsExtendableInMobile: true,
        render: (_, record) =>
            parseStringIntoFormattedStringDate(record.dateTime, 'YYYY-MM-DD', 'DD/MM/YYYY'),
    },
    {
        title: settlementTexts.children.columnLabels.transactionType,
        dataIndex: settlementChildPaymentDTOFields.type,
        key: settlementChildPaymentDTOFields.type,
        render: (value: ChildTransactionListItem['type']) =>
            getChildPaymentTransactionTypeText(value),
    },
    {
        title: settlementTexts.children.columnLabels.paymentFormType,
        dataIndex: settlementChildPaymentDTOFields.paymentMethod,
        key: settlementChildPaymentDTOFields.paymentMethod,
        isVisibleAsExtendableInMobile: true,
        render: (value: ChildTransactionListItem['paymentMethod']) =>
            childPaymentFormTypeLabels[value],
    },
    {
        title: settlementTexts.children.columnLabels.source,
        dataIndex: settlementChildPaymentDTOFields.source,
        key: settlementChildPaymentDTOFields.source,
        isVisibleAsExtendableInMobile: true,
        render: (value: ChildTransactionListItem['source']) => childSourceTypeLabels[value],
    },
    {
        title: settlementTexts.children.columnLabels.balanceTargetType,
        dataIndex: settlementChildPaymentDTOFields.category,
        key: settlementChildPaymentDTOFields.category,
        isVisibleAsExtendableInMobile: true,
        render: (value: ChildTransactionListItem['category']) =>
            childBalanceTargetTypeLabels[value],
    },
    {
        title: commonTexts.dataLabels.amount,
        dataIndex: settlementChildPaymentDTOFields.amount,
        key: settlementChildPaymentDTOFields.amount,
        render: (_, record) => getFormattedPrice(record.amount),
    },
    {
        title: commonTexts.dataLabels.description,
        dataIndex: settlementChildPaymentDTOFields.description,
        key: settlementChildPaymentDTOFields.description,
        isVisibleAsExtendableInMobile: true,
        render: (description: ChildTransactionListItem['description']) => (
            <ChildTransactionDescriptionDisplay description={description} />
        ),
    },
];

export const ChildTransactionTable = ({
    isError,
    isDataInitialLoading,
    isDataRefetching,
    isHiddenAddButton,
    dataSource,
    pagination,
    options,
    onAddButtonClick,
}: ChildTransactionTableProps) => (
    <AppTable
        options={options}
        isError={isError}
        isHiddenAddButton={isHiddenAddButton}
        title={settlementTexts.children.details.paymentTableTitle}
        emptyInfo={settlementTexts.children.details.paymentTableEmpty}
        columns={columns}
        addButtonLabel={settlementTexts.children.actions.addBill}
        onAddButtonClick={onAddButtonClick}
        isTableManageUtilsHidden
        rowKey={({ id }) => id}
        isDataInitialLoading={isDataInitialLoading}
        isDataRefetching={isDataRefetching}
        dataSource={dataSource}
        pagination={pagination}
    />
);
